import { UNAUTHORIZED } from "@/core/constants/error-messages";
import { DASHBOARD_QUERY_ROOT_KEY } from "@/core/constants/dashboard.constants";
import {
  APPROVE_BBGT_ADMIN_PERMISSION,
  CONFIRM_BBGT_PERMISSION,
  DUYET_PHIEU_NHAP_PERMISSION,
  DUYET_PHIEU_XUAT_PERMISSION,
  DUYET_VAT_TU_PERMISSION,
} from "@/core/constants/permissions";
import {
  clearUserFromLocalStorage,
  getUserFromLocalStorage,
  getYearFromLocalStorage,
  setSelectedYearToLocalStorage,
  setUserToLocalStorage,
} from "@/core/helpers/utils";
import { resolveAvatarUrl } from "@/core/helpers/avatar";
import {
  extractFilterDateRange,
  filterDateRangesEqual,
  type FilterDateRange,
} from "@/core/helpers/filter-date-range";
import {
  clampYearToWorkingYears,
  extractWorkingYears,
  getFallbackWorkingYears,
} from "@/core/helpers/working-years";
import {
  getUserSystemSettings,
  saveUserSystemSettings,
} from "@/core/helpers/userSystemSettings";
import type {
  IRoleResponseBase,
  IUserResponse,
} from "@/core/interfaces/ApiResponses";
import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import { UserService } from "@/core/services/UserService";
import { dashboardQueryClient } from "@/core/plugins/vue-query";
import store from "@/store";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
// used as @Module/@Action/@Mutation decorators
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";

function clearDashboardSessionState(context: {
  commit: (type: string, payload?: unknown) => void;
}): void {
  context.commit("DashboardModule/resetDashboardState");
  dashboardQueryClient.removeQueries({
    queryKey: [DASHBOARD_QUERY_ROOT_KEY],
  });
}

function commitFilterDateRangeFromPayload(
  context: { commit: (type: string, payload?: unknown) => void },
  payload: unknown,
  fallbackYear?: string | number | null
): void {
  const year =
    fallbackYear != null && fallbackYear !== ""
      ? Number(fallbackYear)
      : undefined;
  const range = extractFilterDateRange(
    payload,
    Number.isFinite(year) ? year : undefined
  );
  if (range) {
    context.commit(Mutations.SET_FILTER_DATE_RANGE, range);
  }
}

function commitWorkingYearsFromPayload(
  context: { commit: (type: string, payload?: unknown) => void },
  payload: unknown
): void {
  const years = extractWorkingYears(payload);
  if (years?.length) {
    context.commit(Mutations.SET_WORKING_YEARS, years);
  }
}

/**
 * Check a permission against the AuthzModule ability set (single source of truth,
 * fed by GET /api/v1/me/abilities). Falls back to the permissions embedded in the
 * logged-in user payload when abilities have not been loaded yet.
 */
function hasAbility(perm: string, userPermissions?: string[]): boolean {
  const authz = (store.state as { AuthzModule?: { permissions?: Set<string> } })
    .AuthzModule;
  if (authz?.permissions?.has(perm)) {
    return true;
  }
  return (userPermissions || []).includes(perm);
}

export interface User {
  id: number;
  avatar: string;
  email?: string;
  emailVerifiedAt?: string;
  fullName: string;
  positionId: number;
  positionName: string;
  departmentId: number;
  departmentName: string;
  /** HQ name when the user's unit is a branch (cap_don_vi = 2) */
  departmentParentName: string;
  partId?: number;
  partName: string;
  capDonVi: number;
  madonvi: string;
  password: string;
  encodePassword: string;
  phone: string;
  username: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  permissions: string[];
  roles: IRoleResponseBase[];
}

export interface UserAuthInfo {
  errors: unknown;
  user: User;
  authenticationInformation: {
    access_token: string;
    token_type: string;
    expires_in: number;
  };
  isAuthenticated: boolean;
  selectedYear: string | null;
  filterDateRange: FilterDateRange | null;
  workingYears: number[];
}

@Module
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = null;
  user = {} as User;
  authenticationInformation = {} as {
    access_token: string;
    token_type: string;
    expires_in: number;
  };
  isAuthenticated = !!JwtService.getToken();
  settings: Record<string, string> = {};

  selectedYear: string | null = null;
  filterDateRange: FilterDateRange | null = null;
  workingYears: number[] = [];

  get currentUser(): User {
    if (this.user?.id) {
      return this.user;
    }
    return this.user;
  }

  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  get getErrors() {
    return this.errors;
  }

  get getSelectedYear(): string | null {
    // Return the selectedYear state value
    // It should be initialized via SET_USER mutation or SET_YEAR mutation
    return this.selectedYear;
  }

  get getFilterDateRange(): FilterDateRange | null {
    return this.filterDateRange;
  }

  get getWorkingYears(): number[] {
    return this.workingYears.length
      ? this.workingYears
      : getFallbackWorkingYears();
  }

  get canDuyetVatTu(): boolean {
    return hasAbility(DUYET_VAT_TU_PERMISSION, this.currentUser.permissions);
  }

  get roles(): IRoleResponseBase[] {
    return this.currentUser.roles || [];
  }

  get canDuyetPnk(): boolean {
    return hasAbility(DUYET_PHIEU_NHAP_PERMISSION, this.currentUser.permissions);
  }

  get canDuyetPxk(): boolean {
    return hasAbility(DUYET_PHIEU_XUAT_PERMISSION, this.currentUser.permissions);
  }

  get canConfirmBbgt(): boolean {
    return hasAbility(CONFIRM_BBGT_PERMISSION, this.currentUser.permissions);
  }

  get canDuyetBbgt(): boolean {
    return hasAbility(APPROVE_BBGT_ADMIN_PERMISSION, this.currentUser.permissions);
  }

  get capDonVi(): number {
    const envFallback = process.env.VUE_APP_MOCK_CAP_DON_VI
      ? parseInt(process.env.VUE_APP_MOCK_CAP_DON_VI, 10)
      : 2;

    if (this.user?.id && this.user.capDonVi != null) {
      return this.user.capDonVi;
    }

    const stored = getUserFromLocalStorage();
    if (stored) {
      return stored.cap_don_vi ?? stored.capDonVi ?? envFallback;
    }

    return envFallback;
  }

  get isCompanyLevel(): boolean {
    return this.capDonVi === 1;
  }

  get isUnitLevel(): boolean {
    return this.capDonVi === 2;
  }

  @Mutation
  [Mutations.SET_ERROR](error) {
    this.errors = error.message;
  }

  @Mutation
  [Mutations.SET_AUTH](authenticationInformation: {
    access_token: string;
    token_type: string;
    expires_in: number;
  }) {
    this.isAuthenticated = true;

    this.authenticationInformation = { ...authenticationInformation };

    JwtService.saveToken(authenticationInformation.access_token);
  }

  @Mutation
  [Mutations.SET_USER](userResponse: IUserResponse) {
    const envFallback = process.env.VUE_APP_MOCK_CAP_DON_VI
      ? parseInt(process.env.VUE_APP_MOCK_CAP_DON_VI, 10)
      : 2;

    this.user = {
      id: userResponse.id,
      avatar: resolveAvatarUrl(
        userResponse.anh_dai_dien,
        userResponse.anh_dai_dien_url
      ),
      email: userResponse.email,
      emailVerifiedAt: userResponse.email_verified_at,
      fullName: userResponse.ho_va_ten,
      positionId: userResponse.id_chuc_vu,
      positionName: userResponse.chuc_vu?.ten_chuc_vu || '',
      departmentId: userResponse.id_don_vi,
      departmentName: userResponse.don_vi?.ten_don_vi || '',
      departmentParentName: userResponse.don_vi?.ten_don_vi_cha || '',
      partId: userResponse.id_bo_phan,
      partName: userResponse.bo_phan?.ten_bo_phan || '',
      capDonVi: userResponse.cap_don_vi ?? envFallback,
      password: userResponse.mat_khau,
      encodePassword: userResponse.mat_khau_md5,
      phone: userResponse.so_dien_thoai,
      username: userResponse.ten_dang_nhap,
      createdAt: userResponse.created_at,
      updatedAt: userResponse.updated_at,
      permissions: userResponse.permissions || [],
      madonvi: userResponse.ma_don_vi,
      roles: userResponse.roles || [],
    };

    setUserToLocalStorage(userResponse);

    // Initialize selectedYear from localStorage if not already set
    if (this.selectedYear === null) {
      const year = getYearFromLocalStorage();
      this.selectedYear = year;
    }

    const range = extractFilterDateRange(
      userResponse,
      this.selectedYear ? Number(this.selectedYear) : undefined
    );
    if (range) {
      this.filterDateRange = { ...range };
    }

    const years = extractWorkingYears(userResponse);
    if (years?.length) {
      this.workingYears = years;
      if (this.selectedYear) {
        this.selectedYear = clampYearToWorkingYears(
          this.selectedYear,
          years
        );
        setSelectedYearToLocalStorage(this.selectedYear);
      }
    }
  }

  @Mutation
  [Mutations.SET_USER_AVATAR](anhDaiDien: string) {
    if (!this.user?.id) {
      return;
    }

    const avatarUrl = resolveAvatarUrl(anhDaiDien);

    this.user = {
      ...this.user,
      avatar: avatarUrl,
    };

    const stored = getUserFromLocalStorage() || {};
    setUserToLocalStorage({
      ...stored,
      // Keep DB path in storage when the value is already an absolute URL
      anh_dai_dien: stored.anh_dai_dien || anhDaiDien,
      anh_dai_dien_url: avatarUrl,
    });
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;

    this.user = {} as User;

    this.errors = null;

    JwtService.destroyToken();

    clearUserFromLocalStorage();

    this.selectedYear = null;
    this.filterDateRange = null;
    this.workingYears = [];
  }

  @Mutation
  [Mutations.SET_YEAR](year: string) {
    const clamped = clampYearToWorkingYears(
      year,
      this.workingYears.length ? this.workingYears : getFallbackWorkingYears()
    );

    setSelectedYearToLocalStorage(clamped);

    this.selectedYear = clamped;

    const user = this.user;
    if (user?.id) {
      const stored = getUserSystemSettings(user.id);
      if (stored) {
        saveUserSystemSettings(user.id, { ...stored, year: clamped });
      }
    }
  }

  @Mutation
  [Mutations.SET_FILTER_DATE_RANGE](range: FilterDateRange | null) {
    if (!range) {
      this.filterDateRange = null;
      return;
    }
    if (filterDateRangesEqual(this.filterDateRange, range)) {
      return;
    }
    this.filterDateRange = { ...range };
  }

  @Mutation
  [Mutations.SET_WORKING_YEARS](years: number[]) {
    if (!Array.isArray(years) || !years.length) {
      return;
    }

    this.workingYears = [...years];

    if (this.selectedYear) {
      const clamped = clampYearToWorkingYears(this.selectedYear, years);
      if (clamped !== this.selectedYear) {
        this.selectedYear = clamped;
        setSelectedYearToLocalStorage(clamped);

        const user = this.user;
        if (user?.id) {
          const stored = getUserSystemSettings(user.id);
          if (stored) {
            saveUserSystemSettings(user.id, { ...stored, year: clamped });
          }
        }
      }
    }
  }

  @Mutation
  [Mutations.REMOVE_YEAR]() {
    // removeSelectedYearFromLocalStorage();
    this.selectedYear = null;
  }

  @Action({ rawError: true })
  async [Actions.LOGIN]({ phone, password }) {
    this.context.commit(Mutations.SET_ERROR, { message: null });
    clearDashboardSessionState(this.context);

    return UserService.login(phone, password)
      .then((res) => {
        this.context.commit(Mutations.SET_AUTH, res.data.data);
        this.context.commit(Mutations.SET_USER, res.data.data.user);
        commitFilterDateRangeFromPayload(
          this.context,
          res.data.data,
          this.selectedYear
        );
        commitWorkingYearsFromPayload(this.context, res.data.data);

        // Handle app settings from login response
        if (res.data.data.settings) {
          this.context.commit(
            Mutations.SET_APP_SETTINGS,
            res.data.data.settings
          );
        }
      })
      .catch((e) => {
        if (e.response?.data?.error === UNAUTHORIZED) {
          this.context.commit(Mutations.SET_ERROR, {
            message: 'Sai mật khẩu hoặc số điện thoại.',
          });
        } else {
          this.context.commit(Mutations.SET_ERROR, {
            message: 'Có lỗi xảy ra khi đăng nhập.',
          });
        }
      });
  }

  @Action
  [Actions.LOGOUT]() {
    return UserService.logout().finally(() => {
      this.context.commit(Mutations.PURGE_AUTH);
      // Reset app settings when logging out
      this.context.commit(Mutations.RESET_APP_SETTINGS);
      clearDashboardSessionState(this.context);
      // Reset abilities cache on logout
      this.context.dispatch(Actions.RESET_ABILITIES);
    });
  }

  @Action
  [Actions.REGISTER](credentials) {
    return ApiService.post("register", credentials)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_AUTH, data);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, response.data.errors);
      });
  }

  @Action
  [Actions.FORGOT_PASSWORD](payload) {
    return ApiService.post("forgot_password", payload)
      .then(() => {
        this.context.commit(Mutations.SET_ERROR, {});
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, response.data.errors);
      });
  }

  @Action({ rawError: true })
  [Actions.VERIFY_AUTH]() {
    if (!JwtService.getToken()) {
      this.context.commit(Mutations.PURGE_AUTH);
    } else {
      return UserService.refreshToken()
        .then((authenticationResponse) => {
          this.context.commit(
            Mutations.SET_AUTH,
            authenticationResponse.data.data
          );
          this.context.commit(
            Mutations.SET_USER,
            authenticationResponse.data.data.user
          );
          commitFilterDateRangeFromPayload(
            this.context,
            authenticationResponse.data.data,
            this.selectedYear
          );
          commitWorkingYearsFromPayload(
            this.context,
            authenticationResponse.data.data
          );

          // Handle app settings from refresh token response
          if (authenticationResponse.data.data.settings) {
            this.context.commit(
              Mutations.SET_APP_SETTINGS,
              authenticationResponse.data.data.settings
            );
          }
          if (authenticationResponse.data.data.settings) {
            this.context.commit(
              Mutations.SET_APP_SETTINGS,
              authenticationResponse.data.data.settings
            );
          }
        })
        .catch(() => {
          this.context.commit(Mutations.SET_ERROR, {
            message: "Có lỗi xảy ra, cần đăng nhập lại",
          });
          this.context.commit(Mutations.PURGE_AUTH);
        });
    }
  }

  @Action({ rawError: true })
  [Actions.REFRESH_TOKEN]() {
    if (!JwtService.getToken()) {
      this.context.commit(Mutations.PURGE_AUTH);
    } else {
      return UserService.refreshToken()
        .then((authenticationResponse) => {
          this.context.commit(
            Mutations.SET_AUTH,
            authenticationResponse.data.data
          );
          this.context.commit(
            Mutations.SET_USER,
            authenticationResponse.data.data.user
          );
          commitFilterDateRangeFromPayload(
            this.context,
            authenticationResponse.data.data,
            this.selectedYear
          );
          commitWorkingYearsFromPayload(
            this.context,
            authenticationResponse.data.data
          );

          // Handle app settings from refresh token response
          if (authenticationResponse.data.data.settings) {
            this.context.commit(
              Mutations.SET_APP_SETTINGS,
              authenticationResponse.data.data.settings
            );
          }
        })
        .catch(() => {
          this.context.commit(Mutations.SET_ERROR, {
            message: "Có lỗi xảy ra, cần đăng nhập lại",
          });
          this.context.commit(Mutations.PURGE_AUTH);
        });
    }
  }
}
